import React from 'react';
import { cx } from '@linaria/core';
import { Input } from 'api/grpc/resources';
import { flex } from 'styles/utils';
import { Popover, PopoverGroup, PopoverStateReturn } from 'components/Popover';
import { IconCloseSquare } from 'components/Icons';
import { Col, Row } from 'components/Grid';
import { popoverDiv, listItem, title, dataValue, closeIcon, metaDataCss } from 'components/InputInfoPopover/styles';
import { FieldType, getFields } from 'utils/input/helpers';
import { Metadata } from 'modules/InputViewer/components/InputInfo/Metadata';
import { grpcToRestInput } from 'utils/grpc/grpcToRest';
import { CopyButton } from 'components/CopyButton';

interface InputInfoPopoverProps {
  popover: PopoverStateReturn;
  currentInput: Input | null;
}

export const InputInfoPopover = ({ popover, currentInput }: InputInfoPopoverProps): JSX.Element | null => {
  const { unstable_update } = popover;

  if (!currentInput) return null;

  const fields = getFields(currentInput);
  if (!fields) return null;

  const { metadataField, withoutMetadata } = fields.reduce(
    (acc, field) => {
      if (field.label === 'Metadata') {
        acc.metadataField = field;
      } else {
        acc.withoutMetadata.push(field);
      }
      return acc;
    },
    { metadataField: null as FieldType | null, withoutMetadata: [] as FieldType[] },
  );
  const input = grpcToRestInput(currentInput);
  const metadataString = JSON.stringify(input.data.metadata, null, '  ');

  const handleShowMetadata = () => {
    unstable_update();
  };

  return (
    <Popover aria-label="Input Info" title="Input Details" popover={popover} className={popoverDiv}>
      <PopoverGroup>
        <Row className="popover-header">
          <Col xs={11}>
            <h2>Input Details</h2>
          </Col>
          <Col xs={1}>
            <IconCloseSquare className={closeIcon} onClick={() => popover.hide()} size={20} />
          </Col>
        </Row>
        <Row>
          <Col xs={12} style={{ overflow: 'hidden' }}>
            <ul>
              {inputInfoList(withoutMetadata)}
              {metadataField && (
                <li className={cx(listItem, flex.init, metaDataCss)}>
                  <div className={title}>{metadataField?.label}</div>
                  <div className={dataValue}>
                    <Metadata metadataString={metadataString} input={input} onShowMetadata={handleShowMetadata} />
                  </div>
                </li>
              )}
            </ul>
          </Col>
        </Row>
      </PopoverGroup>
    </Popover>
  );
};

const inputInfoList = (fields: FieldType[]): JSX.Element => {
  return (
    <>
      {fields.map(({ label, value }) => {
        return (
          <li key={label} className={cx(listItem, flex.init)}>
            <div className={title}>{label}</div>
            <div className={dataValue}>
              {value || 'n/a'}
              {label === 'Input ID' && <CopyButton text={value as string} key={`copy-${label}`} />}
            </div>
          </li>
        );
      })}
    </>
  );
};
